import AddIcon from '@mui/icons-material/Add';
import LibraryAddRoundedIcon from '@mui/icons-material/LibraryAddRounded';
import LinkIcon from '@mui/icons-material/Link';
import LogoutIcon from '@mui/icons-material/Logout';
import SearchIcon from '@mui/icons-material/Search';
import { Box, Button, Chip, CircularProgress, Dialog, Typography, useTheme } from '@mui/material';
import { captureException } from '@sentry/react';
import { Command } from 'cmdk';
import fuzzysort from 'fuzzysort';
import { useEffect, useMemo, useRef, useState } from 'react';
import { useNavigate } from 'react-router';
import useSWR from 'swr';
import { useDebounceValue } from 'usehooks-ts';

import { useAnalytics } from '~/analytics';
import { PORTFOLIO_ROUTE, SPENDING_ROUTE, TICKETS_ROUTE } from '~/components/common/SideBarLayout';
import { selectedInvoiceIdKey } from '~/components/global/constants';
import { carryGlobalFilters } from '~/components/global/globalFilterParams';
import { CarIcon, InvoiceIcon, PropertyIcon, TicketIcon } from '~/components/icons';
import { useKnowledgeCapture } from '~/components/knowledge/KnowledgeCaptureProvider';
import { searchRetrieve } from '~/data/generated/endpoints/search/search';
import type { KnowledgeTarget } from '~/data/hooks/useKnowledgeTargets';
import { parseApiResponse } from '~/data/parseApiResponse';
import { searchResultsSchema } from '~/data/schemas/search';
import { usePageContext } from '~/hooks/usePageContext';
import { buildUrlForProperty } from '~/utils/urlBuilder';

const MAX_RESULTS = 10;

type CategoryKey = 'all' | 'properties' | 'cars' | 'tickets' | 'invoices';

interface ResultItem {
  id: string;
  label: string;
  link: string;
  knowledgeTarget?: KnowledgeTarget;
}

interface SearchResults {
  cars: ResultItem[];
  properties: ResultItem[];
  invoices: ResultItem[];
  tickets: ResultItem[];
}

const EMPTY_RESULTS: SearchResults = {
  cars: [],
  properties: [],
  invoices: [],
  tickets: [],
};

const CATEGORIES: { key: CategoryKey; label: string }[] = [
  { key: 'all', label: 'All' },
  { key: 'properties', label: 'Properties' },
  { key: 'cars', label: 'Lifts' },
  { key: 'tickets', label: 'Tickets' },
  { key: 'invoices', label: 'Invoices' },
];

type IconComponent = React.ComponentType<{ fontSize?: 'small' | 'medium' | 'large' }>;

interface NavigationAction {
  label: string;
  link: string;
  Icon: IconComponent;
}

const isMac = typeof navigator !== 'undefined' && /Mac|iPhone|iPad|iPod/i.test(navigator.userAgent);
const SHORTCUT_HINT = isMac ? '⌘K' : 'Ctrl+K';

const NAVIGATION_ACTIONS: NavigationAction[] = [
  { label: 'View Portfolios', link: PORTFOLIO_ROUTE, Icon: LinkIcon },
  { label: 'View Tickets', link: TICKETS_ROUTE, Icon: LinkIcon },
  { label: 'View Spendings', link: SPENDING_ROUTE, Icon: LinkIcon },
  { label: 'Logout', link: '/logout', Icon: LogoutIcon },
];

async function runSearch(query: string): Promise<SearchResults> {
  if (!query) {
    return EMPTY_RESULTS;
  }

  try {
    const { data } = await searchRetrieve({ query });
    const { cars, invoices, tickets, properties } = parseApiResponse(
      searchResultsSchema,
      data,
      'Search',
    );

    return {
      cars: cars.flatMap((car) =>
        car.property?.id
          ? [
              {
                id: car.id,
                label: `${car.property.name} - ${car.name}`,
                link: buildUrlForProperty(car.property.id, new URLSearchParams({ carId: car.id })),
                knowledgeTarget: { propertyId: car.property.id, carId: car.id },
              },
            ]
          : [],
      ),
      // Contracts are intentionally omitted from search: a contract spans
      // multiple properties (via PropertyContract), so the API returns only its
      // id/name with no single portfolio link to build. Rather than show a
      // "Contracts" filter that always returns zero hits, the category is left
      // out entirely until a dedicated contract route exists.
      properties: fuzzysort
        .go(query, properties, { key: 'name', limit: MAX_RESULTS })
        .map(({ obj }) => ({
          id: obj.id,
          label: obj.name,
          link: buildUrlForProperty(obj.id),
          knowledgeTarget: { propertyId: obj.id },
        })),
      invoices: fuzzysort
        .go(query, invoices, { key: 'number', limit: MAX_RESULTS })
        .map(({ obj }) => ({
          id: obj.id,
          label: obj.property?.name ? `${obj.property.name} - ${obj.number}` : obj.number,
          link: `?${selectedInvoiceIdKey}=${obj.id}`,
        })),
      tickets: fuzzysort
        .go(query, tickets, { key: 'number', limit: MAX_RESULTS })
        .map(({ obj }) => ({
          id: obj.id,
          label: obj.property?.name ? `${obj.property.name} - ${obj.number}` : obj.number,
          link: `/tickets?ticketId=${obj.id}`,
        })),
    };
  } catch (error) {
    captureException(error);
    return EMPTY_RESULTS;
  }
}

/**
 * Where a result actually sends the reader: its own link, plus the global filters the
 * page it was opened from was showing.
 *
 * A result carries only what identifies the record — `?selectedInvoiceId=<id>` for an
 * invoice, `/properties/<id>?carId=<id>` for a lift — so resolving it against the
 * current pathname alone replaced the whole query string. The reader's portfolio and
 * period went with it, and the app shell then redirected the destination to its default
 * range, leaving the URL, the Period chip and the API call disagreeing.
 *
 * The link wins wherever it names a filter itself.
 */
export function resolveResultLink(
  link: string,
  current: { pathname: string; search: string },
): string {
  const url = new URL(
    link.startsWith('?') ? current.pathname + link : link,
    'https://liftai.local',
  );
  url.search = carryGlobalFilters(url.searchParams, new URLSearchParams(current.search)).toString();

  return `${url.pathname}${url.search}${url.hash}`;
}

// Short prefixes are left to search, so "Knoxville" doesn't select Add knowledge.
const KNOWLEDGE_COMMAND_WORDS = ['knowledge', 'note', 'decision', 'commitment'];

const matchesKnowledgeCommand = (query: string) => {
  const q = query.trim().toLowerCase();
  if (!q) return true;
  const words = q.split(/\s+/);
  return (
    (q.length >= 3 && ('add knowledge'.startsWith(q) || 'knowledge'.startsWith(q))) ||
    KNOWLEDGE_COMMAND_WORDS.some((word) => words.includes(word))
  );
};

/** Scoped to the current property or elevator page; elsewhere opens the picker. */
function KnowledgeCommands({
  query,
  openAddKnowledge,
  onClose,
  onTrack,
}: {
  query: string;
  openAddKnowledge: NonNullable<ReturnType<typeof useKnowledgeCapture>>['openAddKnowledge'];
  onClose: () => void;
  onTrack: (label: string) => void;
}) {
  const pageContext = usePageContext();
  if (!matchesKnowledgeCommand(query)) return null;

  const { entityType, propertyId, carId, label } = pageContext;
  if (entityType && propertyId && label) {
    const Icon = entityType === 'car' ? CarIcon : PropertyIcon;
    return (
      <Command.Group heading={label}>
        <Command.Item
          value={`knowledge:add:${carId ?? propertyId}`}
          onSelect={() => {
            onTrack(`Add knowledge (${entityType})`);
            onClose();
            openAddKnowledge({ target: { propertyId, carId } });
          }}
        >
          <Icon fontSize="small" />
          <span>Add knowledge to {label}</span>
        </Command.Item>
      </Command.Group>
    );
  }

  return (
    <Command.Group heading="Commands">
      <Command.Item
        value="knowledge:add"
        onSelect={() => {
          onTrack('Add knowledge');
          onClose();
          openAddKnowledge();
        }}
      >
        <LibraryAddRoundedIcon fontSize="small" />
        <span>Add knowledge…</span>
      </Command.Item>
    </Command.Group>
  );
}

interface Group {
  key: CategoryKey;
  name: string;
  items: ResultItem[];
  Icon: IconComponent;
}

interface CommandPaletteProps {
  onClose: () => void;
}

export function CommandPalette({ onClose }: CommandPaletteProps) {
  const navigate = useNavigate();
  const theme = useTheme();
  const [query, setQuery] = useState('');
  const [category, setCategory] = useState<CategoryKey>('all');
  const [debouncedQuery] = useDebounceValue(query, 200);
  const { track } = useAnalytics();
  const knowledgeCapture = useKnowledgeCapture();
  const knowledgeCommands =
    knowledgeCapture && category === 'all' ? (
      <KnowledgeCommands
        query={query}
        openAddKnowledge={knowledgeCapture.openAddKnowledge}
        onClose={onClose}
        onTrack={(label) => track('command_palette_navigation_clicked', { action_label: label })}
      />
    ) : null;
  const { data: results = EMPTY_RESULTS, isValidating: loading } = useSWR(
    ['command-palette-search', debouncedQuery],
    ([, q]) => runSearch(q),
    { keepPreviousData: true, revalidateOnFocus: false },
  );

  // Keep `category` *out* of the dependency array: it's a filter on the
  // already-loaded result set, not a trigger for a new search. Reading
  // it via ref keeps it on the event payload without firing the event
  // whenever the user toggles category chips.
  const categoryRef = useRef(category);
  useEffect(() => {
    categoryRef.current = category;
  }, [category]);
  useEffect(() => {
    if (!debouncedQuery || loading) return;
    const resultCount =
      results.cars.length +
      results.properties.length +
      results.invoices.length +
      results.tickets.length;
    track('command_palette_search_executed', {
      category: categoryRef.current,
      query_length: debouncedQuery.length,
      result_count: resultCount,
    });
  }, [debouncedQuery, loading, results, track]);

  const handleSelect = (link: string, meta: { resultType: string; group: string }) => {
    track('command_palette_result_clicked', {
      result_type: meta.resultType,
      group: meta.group,
    });
    void navigate(resolveResultLink(link, window.location));
    onClose();
  };

  const handleNavigationSelect = (action: NavigationAction) => {
    track('command_palette_navigation_clicked', { action_label: action.label });
    const url = new URL(action.link, window.location.origin);
    void navigate(url.toString().replace(window.location.origin, ''));
    onClose();
  };

  const allGroups: Group[] = useMemo(
    () => [
      { key: 'properties', name: 'Properties', items: results.properties, Icon: PropertyIcon },
      { key: 'cars', name: 'Lifts', items: results.cars, Icon: CarIcon },
      { key: 'tickets', name: 'Tickets', items: results.tickets, Icon: TicketIcon },
      { key: 'invoices', name: 'Invoices', items: results.invoices, Icon: InvoiceIcon },
    ],
    [results],
  );

  const visibleGroups =
    category === 'all' ? allGroups : allGroups.filter((g) => g.key === category);

  const hasResults = visibleGroups.some((g) => g.items.length > 0);
  const showNavigation = query.length === 0 && category === 'all';

  return (
    <Dialog
      open
      onClose={onClose}
      slotProps={{
        paper: {
          sx: {
            width: '100%',
            maxWidth: 640,
            mt: '10vh',
            alignSelf: 'flex-start',
            overflow: 'hidden',
          },
        },
      }}
    >
      <Box
        sx={{
          '[cmdk-group-heading]': {
            px: 1.5,
            py: 1,
            fontSize: 12,
            fontWeight: 600,
            color: 'text.secondary',
            textTransform: 'uppercase',
            letterSpacing: '0.5px',
          },
          '[cmdk-item]': {
            display: 'flex',
            alignItems: 'center',
            gap: 1.5,
            px: 1.5,
            py: 1,
            borderRadius: 1,
            cursor: 'pointer',
            color: 'text.primary',
          },
          '[cmdk-item][data-selected="true"]': {
            bgcolor: 'action.hover',
          },
        }}
      >
        <Command shouldFilter={false} loop label="Search">
          <Box
            sx={{
              display: 'flex',
              alignItems: 'center',
              gap: 1,
              px: 2,
              py: 1.5,
              borderBottom: `1px solid ${theme.palette.divider}`,
            }}
          >
            <SearchIcon fontSize="small" sx={{ color: 'text.secondary' }} />
            <Command.Input
              value={query}
              onValueChange={setQuery}
              placeholder="Search or type a command…"
              style={{
                flex: 1,
                border: 'none',
                outline: 'none',
                background: 'transparent',
                fontSize: theme.typography.body1.fontSize as string,
                fontFamily: theme.typography.fontFamily,
                color: theme.palette.text.primary,
              }}
              // eslint-disable-next-line jsx-a11y/no-autofocus -- intentional: opening the palette is a deliberate user action
              autoFocus
            />
            {loading ? <CircularProgress size={16} /> : null}
          </Box>

          <Box
            sx={{
              display: 'flex',
              gap: 1,
              px: 2,
              py: 1.25,
              borderBottom: `1px solid ${theme.palette.divider}`,
              overflowX: 'auto',
            }}
          >
            {CATEGORIES.map(({ key, label }) => {
              const selected = category === key;
              return (
                <Chip
                  key={key}
                  label={label}
                  size="small"
                  onClick={() => setCategory(key)}
                  color={selected ? 'primary' : 'default'}
                  variant={selected ? 'filled' : 'outlined'}
                />
              );
            })}
          </Box>

          <Command.List style={{ maxHeight: 360, overflowY: 'auto', padding: 8 }}>
            {/* While searching they go last, so Enter still opens the top result. */}
            {query.length === 0 ? knowledgeCommands : null}

            {showNavigation ? (
              <Command.Group heading="Navigation">
                {NAVIGATION_ACTIONS.map((action) => (
                  <Command.Item
                    key={action.label}
                    value={`navigation:${action.label}`}
                    onSelect={() => handleNavigationSelect(action)}
                  >
                    <action.Icon fontSize="small" />
                    <span>{action.label}</span>
                  </Command.Item>
                ))}
              </Command.Group>
            ) : null}

            {query.length === 0 && category !== 'all' ? (
              <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                Start typing to search{' '}
                {CATEGORIES.find((c) => c.key === category)?.label.toLowerCase()}…
              </Typography>
            ) : null}

            {query.length > 0 && !loading && !hasResults ? (
              <Command.Empty>
                <Typography variant="body2" color="text.secondary" sx={{ px: 1.5, py: 1 }}>
                  No results for &quot;{query}&quot;
                </Typography>
              </Command.Empty>
            ) : null}

            {visibleGroups.map((group) =>
              group.items.length > 0 ? (
                <Command.Group key={group.name} heading={group.name}>
                  {group.items.map((item) => {
                    const Icon = group.Icon;
                    return (
                      <Command.Item
                        key={`${group.name}:${item.id}`}
                        value={`${group.name}:${item.id}:${item.label}`}
                        onSelect={() =>
                          handleSelect(item.link, {
                            resultType: group.key,
                            group: group.name,
                          })
                        }
                      >
                        <Icon fontSize="small" />
                        <Box component="span" sx={{ flex: 1, minWidth: 0 }}>
                          {item.label}
                        </Box>
                        {knowledgeCapture && item.knowledgeTarget ? (
                          <Button
                            size="small"
                            startIcon={<AddIcon />}
                            aria-label={`Add knowledge to ${item.label}`}
                            onPointerDown={(e) => e.stopPropagation()}
                            onClick={(e) => {
                              e.stopPropagation();
                              const target = item.knowledgeTarget;
                              track('command_palette_navigation_clicked', {
                                action_label: `Add knowledge (${target?.carId ? 'car' : 'property'} result)`,
                              });
                              onClose();
                              knowledgeCapture.openAddKnowledge({ target });
                            }}
                            sx={{
                              flexShrink: 0,
                              alignSelf: 'stretch',
                              my: -1,
                              mr: -1.5,
                              px: 2.5,
                              borderRadius: 0,
                              borderTopRightRadius: 4,
                              borderBottomRightRadius: 4,
                              textTransform: 'none',
                              fontSize: 12,
                            }}
                          >
                            Add knowledge
                          </Button>
                        ) : null}
                      </Command.Item>
                    );
                  })}
                </Command.Group>
              ) : null,
            )}

            {query.length > 0 ? knowledgeCommands : null}
          </Command.List>

          <Box
            sx={{
              display: 'flex',
              justifyContent: 'flex-end',
              alignItems: 'center',
              px: 2,
              py: 1,
              borderTop: `1px solid ${theme.palette.divider}`,
            }}
          >
            <Box
              component="kbd"
              sx={{
                px: 0.75,
                py: 0.25,
                fontSize: 11,
                fontFamily: 'monospace',
                bgcolor: 'action.hover',
                borderRadius: 0.5,
                color: 'text.secondary',
              }}
            >
              {SHORTCUT_HINT}
            </Box>
          </Box>
        </Command>
      </Box>
    </Dialog>
  );
}
